import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Briefcase, PlusCircle, Edit, Trash2, GripVertical, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

export function AdminBusinessTypesTab() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState(null); // { old_name, new_name }
  const [dragIdx, setDragIdx] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/business-types`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setTypes(d.business_types || []);
      }
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/business-types`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      toast.success('Business type added');
      setNewName('');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const handleSaveRename = async () => {
    if (!editing || !editing.new_name.trim() || editing.new_name === editing.old_name) {
      setEditing(null); return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/business-types`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ old_name: editing.old_name, new_name: editing.new_name.trim() }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      const data = await res.json();
      toast.success(`Renamed (${data.companies_updated || 0} companies updated)`);
      setEditing(null);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (name) => {
    if (!(await window.appConfirm(
      `Delete "${name}"?\n\nAny companies tagged with this type will be cleared. This cannot be undone.`
    ))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/business-types/${encodeURIComponent(name)}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      toast.success(`Deleted (${data.companies_cleared || 0} companies cleared)`);
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDrop = async (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); return; }
    const next = [...types];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setTypes(next);
    setDragIdx(null);
    try {
      await fetch(`${BACKEND_URL}/api/admin/business-types/order`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ names: next }),
      });
      toast.success('Order saved');
    } catch { toast.error('Failed to save order'); load(); }
  };

  return (
    <div className="space-y-4" data-testid="admin-business-types-tab">
      <div>
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-brand-navy" /> Business Types
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Master list used by company profiles, the company directory filter and reports. Drag to reorder, rename to cascade across all tagged companies.
        </p>
      </div>

      <Card className="p-3 flex items-end gap-2">
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-600">Add a new business type</label>
          <Input value={newName} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
            placeholder="e.g. Logistics SaaS" className="mt-1"
            data-testid="business-type-new-input" />
        </div>
        <Button onClick={handleCreate} disabled={!newName.trim()} className="bg-brand-navy text-white"
          data-testid="business-type-add-btn">
          <PlusCircle className="h-4 w-4 mr-2" /> Add
        </Button>
      </Card>

      {loading ? (
        <p className="text-center text-sm text-slate-400 py-8">Loading…</p>
      ) : types.length === 0 ? (
        <Card className="p-6 text-center text-sm text-slate-500">No business types yet — add the first one above.</Card>
      ) : (
        <div className="grid gap-2" data-testid="business-types-list">
          {types.map((name, idx) => (
            <Card key={name}
              draggable
              onDragStart={() => setDragIdx(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(idx)}
              onDragEnd={() => setDragIdx(null)}
              className={`p-3 flex items-center gap-3 transition-all ${dragIdx === idx ? 'opacity-40 scale-[0.99]' : ''} cursor-grab active:cursor-grabbing`}
              data-testid={`business-type-${name}`}
            >
              <GripVertical className="h-4 w-4 text-slate-300 flex-shrink-0" />
              {editing?.old_name === name ? (
                <div className="flex-1 flex gap-2">
                  <Input
                    autoFocus
                    value={editing.new_name}
                    onChange={e => setEditing({ ...editing, new_name: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') handleSaveRename(); if (e.key === 'Escape') setEditing(null); }}
                    className="h-8 text-sm"
                  />
                  <Button size="sm" onClick={handleSaveRename} className="h-8 bg-brand-navy text-white">
                    <Save className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="h-8">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <>
                  <span className="flex-1 text-sm font-medium text-slate-700">{name}</span>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                    onClick={() => setEditing({ old_name: name, new_name: name })}
                    data-testid={`business-type-edit-${name}`}>
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500"
                    onClick={() => handleDelete(name)}
                    data-testid={`business-type-delete-${name}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
